import Seo from "../components/Seo";
import Header from "../components/tscm/Header";
import Footer from "../components/tscm/Footer";
import Contact from "../components/tscm/Contact";
import { scrollToId } from "../lib/scroll";
import { PHONE_DISPLAY, PHONE_TEL } from "../constants/site";
import {
  Breadcrumb,
  BeforeYouCall,
  ProgrammeLinks,
  SubpageHero,
  Section,
  CoverList,
  NumberedList,
  StepList,
} from "./SubpageBlocks";

const LD = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Live meeting monitoring",
    description:
      "Live TSCM monitoring: a small LAT team watches the radio spectrum during a sensitive meeting, as an add-on to a physical inspection. Law Abiding Technology.",
    areaServed: "India",
    provider: { "@id": "https://tscmservices.in/#org" },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://tscmservices.in/" },
      { "@type": "ListItem", position: 2, name: "Live meeting monitoring", item: "https://tscmservices.in/live-meeting-monitoring" },
    ],
  },
];

export default function LiveMonitoring() {
  return (
    <div id="top" className="min-h-screen bg-base-color font-body text-ink">
      <Seo
        title="Live Meeting Monitoring — On-Site TSCM | TSCM Services"
        description="Live TSCM monitoring: a small LAT team watches the radio spectrum during a sensitive meeting, as an add-on to a physical inspection. Law Abiding Technology."
        path="/live-meeting-monitoring"
        jsonLd={LD}
      />
      <Header />
      <main>
        <div className="site-x pt-6">
          <Breadcrumb current="Live meeting monitoring" />
        </div>
        <SubpageHero
          tag="Live meeting monitoring · During the meeting"
          lines={["The spectrum held under", "observation while", "the meeting runs."]}
          image="/media/monitoring-v2pro-spectrum.jpg"
          imageAlt="Operator watching a live spectrum dashboard on a rugged tablet while a meeting runs"
        >
          <p>
            Live meeting monitoring is an add-on to an on-site TSCM inspection
            (a bug sweep). It is for when the inspection is done and you
            remain unsure, or suspect that someone attending may carry a
            device, knowingly or not, that transmits live during the meeting.
            A small team from Law Abiding Technology (LAT) stays on site and
            watches the radio frequencies coming from the room for the length
            of the session. If it is commissioned, it is included in the
            written report.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-5">
            <button
              onClick={() => scrollToId("contact")}
              data-testid="subpage-request-button"
              className="rounded-full bg-violet px-6 py-3 text-[0.9375rem] font-semibold text-[#1a1021] transition-colors hover:bg-violeth"
            >
              Request a confidential consultation
            </button>
            <a
              href={PHONE_TEL}
              data-testid="subpage-phone-link"
              className="text-sm text-sub underline decoration-hairline underline-offset-4 transition-colors hover:text-ink"
            >
              or call {PHONE_DISPLAY}
            </a>
          </div>
        </SubpageHero>

        <Section title="What it covers." wide>
          <CoverList
            items={[
              "The spectrum held under observation for the full session, within instrument range",
              "A small team on site throughout",
              "For the discussions that matter most",
            ]}
          />
        </Section>

        <Section title="What it does not do.">
          <p className="text-base leading-relaxed text-sub">
            Monitoring watches the radio spectrum for the length of the
            session. It does not replace the physical inspection, and it
            cannot detect a device that does not transmit.
          </p>
        </Section>

        <Section title="For the rooms where it matters most.">
          <NumberedList
            items={[
              "After the inspection, when you remain unsure",
              "When you suspect someone attending may carry a device, maliciously or unknowingly",
            ]}
          />
          <p className="mt-8 text-base leading-relaxed text-sub">
            A device that transmits and is brought into the room during the
            meeting is not covered by a check done beforehand. Monitoring is
            the arrangement for watching while the meeting runs.
          </p>
        </Section>

        <Section title="How an engagement runs." wide>
          <StepList
            steps={[
              { bold: "A scoping call", rest: ": the venue and the time we are to attend." },
              { bold: "An on-site inspection first.", rest: "" },
              { bold: "Through the meeting", rest: ": a small LAT team stays on site and watches the radio frequencies from the room." },
              { bold: "The written report", rest: " includes the monitoring." },
            ]}
          />
        </Section>

        <BeforeYouCall />
        <ProgrammeLinks
          links={[
            { href: "/corporate-tscm", tag: "Reactive", title: "Corporate TSCM inspection" },
            { href: "/preventive-tscm", tag: "Before the meeting", title: "Preventive TSCM" },
            { href: "/#contact", tag: "Ongoing", title: "Ask about an annual contract" },
          ]}
        />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
